import Close from '@mui/icons-material/Close';
import FilterListAlt from '@mui/icons-material/FilterListAlt';
import { Box, Button, FormControl, IconButton, MenuItem, Modal, Select, Stack, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { memo, useState } from 'react';

import type { AdminUserAutocompleteResult } from '@/hooks/autocomplete/useAdminUsersAutocomplete';
import type { UserAutocompleteResult } from '@/hooks/autocomplete/useUsersAutocomplete';
import type { AuditLogFilters } from '@/hooks/useAuditLogs';
import theme from '@/theme';

import GroupNameAutocomplete, { type AuditLogGroupType } from './GroupNameAutocomplete';
import UserAdminAutocomplete from './UserAdminAutocomplete';
import UserAutocomplete from './UserAutocomplete';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: '#FFFFFF',
  boxShadow: 24,
  p: 0,
} as const;

const labelStyles = {
  fontSize: 12,
  textTransform: 'uppercase',
  lineHeight: '16px',
  fontWeight: 400,
  color: theme.hhglobal.purpose.textWeak,
  paddingBottom: '4px',
} as const;

const selectStyles = {
  '& .MuiFormControl-root': {
    marginBottom: theme.hhglobal.spacing.spacing[2].valuePx,
  },
  height: 36,
  width: '100%',
  fontSize: theme.hhglobal.typography.body.large.fontSize,
  '& .MuiOutlinedInput-input': {
    borderColor: theme.hhglobal.purpose.strokeStrong,
    paddingTop: theme.hhglobal.spacing.spacing[1].valuePx,
    paddingBottom: theme.hhglobal.spacing.spacing[1].valuePx,
    paddingLeft: theme.hhglobal.spacing.spacing[2].valuePx,
    paddingRight: theme.hhglobal.spacing.spacing[2].valuePx,
    height: '28px',
    '&.MuiSelect-outlined': {
      height: 'auto',
    },
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.hhglobal.purpose.strokeStrong,
    borderWidth: '1px',
  },
} as const;

/* For now we overwrite brand color incoming from theme. TBD we need global solution for the theme as for now have all figma configuration, not adjusted for
HubNexus setup */
const datePickerStyle = {
  '& .MuiPickersInputBase-colorPrimary.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: `${theme.hhglobal.purpose.strokeStrong} !important`,
  },
} as const;

const pickerSelectedStyle = {
  '&.MuiPickersDay-today': {
    backgroundColor: `${theme.hhglobal.purpose.fillWeak} !important`,
  },
  '&.Mui-selected': {
    backgroundColor: `${theme.hhglobal.purpose.strokeWeak} !important`,
  },
  '&.Mui-selected:hover': {
    backgroundColor: `${theme.hhglobal.purpose.strokeStrong} !important`,
  },
  '&:hover': {
    backgroundColor: `${theme.hhglobal.purpose.fillWeak} !important`,
  },
} as const;

export interface AuditLogsFilterProps {
  organizationId: number | null;
  appliedFilters: AuditLogFilters | null;
  onChange: (filters: AuditLogFilters | null) => void;
}

function AuditLogsFilterComponent({ organizationId, appliedFilters, onChange }: AuditLogsFilterProps) {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [startDateHasError, setStartDateHasError] = useState(false);
  const [endDateHasError, setEndDateHasError] = useState(false);
  const [groupType, setGroupType] = useState<AuditLogGroupType | ''>('');
  const [selectedGroupNames, setSelectedGroupNames] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<UserAutocompleteResult[]>([]);
  const [selectedPerformedBy, setSelectedPerformedBy] = useState<AdminUserAutocompleteResult[]>([]);

  const shouldFetchAutocomplete = open && !!organizationId && organizationId > 0;
  const isDateRangeInvalid = startDate != null && endDate != null && startDate.isAfter(endDate);

  const hasDateError = isDateRangeInvalid || (startDate != null && startDateHasError) || (endDate != null && endDateHasError);

  const sanitizedSelectedUserIds = selectedUserIds.map(({ email }) => email).filter((email): email is string => email != null);

  const sanitizedSelectedPerformedBy = selectedPerformedBy.map(({ email }) => email).filter((email): email is string => email != null);

  const hasFilters =
    startDate != null ||
    endDate != null ||
    groupType !== '' ||
    selectedGroupNames.length > 0 ||
    sanitizedSelectedUserIds.length > 0 ||
    sanitizedSelectedPerformedBy.length > 0;

  const clearForm = () => {
    setStartDate(null);
    setEndDate(null);
    setGroupType('');
    setSelectedGroupNames([]);
    setSelectedUserIds([]);
    setSelectedPerformedBy([]);
  };

  const resetFormToApplied = () => {
    if (appliedFilters) {
      setStartDate(appliedFilters.startDate ? dayjs(appliedFilters.startDate).utc() : null);
      setEndDate(appliedFilters.endDate ? dayjs(appliedFilters.endDate).utc() : null);
      setGroupType((appliedFilters.groupType as AuditLogGroupType) ?? '');
      setSelectedGroupNames(appliedFilters.groupNames ?? []);
      setSelectedUserIds((appliedFilters.userIds ?? []).map((email) => ({ email })));
      setSelectedPerformedBy((appliedFilters.performedBy ?? []).map((email) => ({ email })));
    } else {
      clearForm();
    }
  };

  const handleClearAll = () => {
    clearForm();
  };

  const handleOpen = () => {
    resetFormToApplied();
    setOpen(true);
  };

  const handleClose = () => {
    resetFormToApplied();
    setOpen(false);
  };

  const handleApplyFilters = () => {
    if (hasDateError) {
      return;
    }

    const filters: AuditLogFilters = {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      groupType: groupType || undefined,
      groupNames: selectedGroupNames.length > 0 ? selectedGroupNames : undefined,
      userIds: selectedUserIds.length > 0 ? selectedUserIds.map((u) => u.email).filter((e): e is string => !!e) : undefined,
      performedBy: selectedPerformedBy.length > 0 ? selectedPerformedBy.map((u) => u.email).filter((e): e is string => !!e) : undefined,
    };

    onChange(hasFilters ? filters : null);
    handleClose();
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <IconButton
          data-testid='audit-filter-button'
          aria-label='Filter audit logs'
          sx={{
            border: (theme) => `1px solid ${theme.hhglobal.neutral.gray400}`,
            borderRadius: '4px',
            width: '32px',
            height: '32px',
          }}
          onClick={handleOpen}
        >
          <FilterListAlt fontSize='small' />
        </IconButton>
        {appliedFilters && (
          <Box
            data-testid='audit-filter-badge'
            aria-hidden
            sx={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: theme.hhglobal.neutral.superblack,
            }}
          />
        )}
      </Box>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='filter-audit-logs-modal-title'
        aria-describedby='filter-audit-logs-modal-description'
        data-testid='audit-filter-modal'
      >
        <Box sx={style}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: theme.hhglobal.purpose.fillWeak,
              paddingTop: theme.hhglobal.spacing.spacing[3].valuePx,
              paddingBottom: theme.hhglobal.spacing.spacing[3].valuePx,
              paddingRight: theme.hhglobal.spacing.spacing[6].valuePx,
              paddingLeft: theme.hhglobal.spacing.spacing[6].valuePx,
              height: '48px',
            }}
          >
            <Typography id='filter-audit-logs-modal-title' variant='h6' component='h2'>
              Filter
            </Typography>
            <IconButton
              aria-label='Close filter dialog'
              data-testid='audit-filter-close-button'
              onClick={handleClose}
              size='small'
              sx={{
                marginLeft: theme.hhglobal.spacing.spacing[4].valuePx,
              }}
            >
              <Close fontSize='small' />
            </IconButton>
          </Box>
          <Box
            sx={{
              paddingTop: theme.hhglobal.spacing.spacing[3].valuePx,
              paddingBottom: theme.hhglobal.spacing.spacing[3].valuePx,
              paddingRight: theme.hhglobal.spacing.spacing[6].valuePx,
              paddingLeft: theme.hhglobal.spacing.spacing[6].valuePx,
            }}
          >
            <Box>
              <Typography
                id='time-period-title'
                variant='h6'
                component='h2'
                sx={{
                  fontWeight: theme.hhglobal.typography.fontWeights.bold,
                  paddingBottom: theme.hhglobal.spacing.spacing[2].valuePx,
                }}
              >
                Time Period
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: theme.hhglobal.spacing.spacing[8].valuePx,
                  marginBottom: theme.hhglobal.spacing.spacing[3].valuePx,
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl sx={{ width: '100%' }} data-testid='audit-filter-start-date'>
                    <Typography component='span' sx={labelStyles}>
                      Start Date
                    </Typography>
                    <DatePicker
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      format='DD/MM/YYYY'
                      timezone='UTC'
                      disableFuture
                      onError={(reason: unknown) => {
                        setStartDateHasError(Boolean(reason));
                      }}
                      slotProps={{
                        day: {
                          sx: pickerSelectedStyle,
                        },
                        yearButton: { sx: pickerSelectedStyle },
                        textField: {
                          size: 'small',
                          error: hasDateError && startDate != null,
                        },
                      }}
                      sx={datePickerStyle}
                    />
                  </FormControl>
                  <FormControl sx={{ width: '100%' }} data-testid='audit-filter-end-date'>
                    <Typography component='span' sx={labelStyles}>
                      End Date
                    </Typography>
                    <DatePicker
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      format='DD/MM/YYYY'
                      timezone='UTC'
                      disableFuture
                      onError={(reason: unknown) => {
                        setEndDateHasError(Boolean(reason));
                      }}
                      slotProps={{
                        day: {
                          sx: pickerSelectedStyle,
                        },
                        yearButton: {
                          sx: pickerSelectedStyle,
                        },
                        textField: {
                          size: 'small',
                          error: hasDateError && endDate != null,
                        },
                      }}
                      sx={datePickerStyle}
                    />
                  </FormControl>
                </LocalizationProvider>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                '& .MuiStack-root': {
                  width: '100%',
                },
                gap: theme.hhglobal.spacing.spacing[2].valuePx,
                marginBottom: theme.hhglobal.spacing.spacing[3].valuePx,
              }}
            >
              <Typography
                id='group-details-title'
                variant='h6'
                component='h2'
                sx={{
                  fontWeight: theme.hhglobal.typography.fontWeights.bold,
                }}
              >
                Group Details
              </Typography>
              <FormControl fullWidth data-testid='audit-filter-group-type'>
                <Typography component='span' sx={labelStyles}>
                  Group Type
                </Typography>
                <Select
                  displayEmpty
                  value={groupType}
                  onChange={(event) => {
                    const newGroupType = event.target.value as AuditLogGroupType | '';
                    setGroupType(newGroupType);
                    setSelectedGroupNames([]);
                  }}
                  renderValue={(value) => {
                    if (!value) return 'Select Group Type';
                    if (value === 'AllocationGroup') return 'Allocation Group';
                    if (value === 'BudgetGroup') return 'Budget Group';
                    if (value === 'DistributorGroup') return 'Distributor Group';
                    return value;
                  }}
                  sx={selectStyles}
                  data-testid='audit-filter-group-type-select'
                >
                  <MenuItem value=''>Select Group Type</MenuItem>
                  <MenuItem value='AllocationGroup'>Allocation Group</MenuItem>
                  <MenuItem value='BudgetGroup'>Budget Group</MenuItem>
                  <MenuItem value='DistributorGroup'>Distributor Group</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ '& > div': { width: '100%' } }} data-testid='audit-filter-group-name'>
                <GroupNameAutocomplete
                  organizationId={organizationId ?? 0}
                  groupType={groupType}
                  value={selectedGroupNames}
                  onChange={(_, newValue) => setSelectedGroupNames(newValue)}
                  label='Group Name'
                  enabled={shouldFetchAutocomplete}
                  disabled={!organizationId}
                  width='100%'
                />
              </FormControl>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.hhglobal.spacing.spacing[2].valuePx,
                '& .MuiStack-root': {
                  width: '100%',
                },
              }}
            >
              <Typography
                id='user-activity-title'
                variant='h6'
                component='h2'
                sx={{
                  fontWeight: theme.hhglobal.typography.fontWeights.bold,
                }}
              >
                User Activity
              </Typography>
              <FormControl fullWidth sx={{ '& > div': { width: '100%' } }} data-testid='audit-filter-user-emails'>
                <UserAutocomplete
                  organizationId={organizationId ?? 0}
                  value={selectedUserIds}
                  onChange={(_, newValue) => setSelectedUserIds(newValue)}
                  label='User Emails'
                  enabled={shouldFetchAutocomplete}
                  disabled={!organizationId}
                  width='100%'
                />
              </FormControl>
              <FormControl fullWidth sx={{ '& > div': { width: '100%' } }} data-testid='audit-filter-performed-by'>
                <UserAdminAutocomplete
                  value={selectedPerformedBy}
                  onChange={(_, newValue) => setSelectedPerformedBy(newValue)}
                  label='Performed By (Admin Emails)'
                  enabled={shouldFetchAutocomplete}
                  disabled={!organizationId}
                  width='100%'
                />
              </FormControl>
            </Box>
          </Box>
          <Box
            sx={{
              paddingTop: theme.hhglobal.spacing.spacing[3].valuePx,
              paddingBottom: theme.hhglobal.spacing.spacing[3].valuePx,
              paddingRight: theme.hhglobal.spacing.spacing[6].valuePx,
              paddingLeft: theme.hhglobal.spacing.spacing[6].valuePx,
              height: '60px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant='outlined'
              color='primary'
              onClick={handleClearAll}
              disabled={!hasFilters}
              data-testid='audit-filter-clear-all-button'
            >
              Clear All
            </Button>
            <Stack direction='row' spacing={theme.hhglobal.spacing.spacing[3].valuePx}>
              <Button variant='outlined' color='primary' onClick={handleClose} data-testid='audit-filter-cancel-button'>
                Cancel
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={handleApplyFilters}
                disabled={hasDateError}
                data-testid='audit-filter-apply-button'
              >
                Apply Filters
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

const AuditLogsFilter = memo(AuditLogsFilterComponent);

export default AuditLogsFilter;
